<script lang="ts">
  import { _ } from 'svelte-i18n';
  import {
    branchOfficeCountMin,
    cantons,
    capital,
    centerPostalCode,
    companySizeRange,
    foundingYearRange,
    hasEmail,
    hasFacebook,
    hasInstagram,
    hasLinkedin,
    hasPhone,
    hasRevisionsverzicht,
    hasTwitter,
    hasWebsite,
    languages,
    legalForm,
    excludeLegalForm,
    postalCodes,
    radiusKm,
    revisionCompany,
    textConstraints,
    signalFilters
  } from '$lib/stores/selection';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import {
    technologies,
    mailGaps,
    certifications,
    groupRoles,
    salesModels,
    fleetFilter
  } from '$lib/stores/selection';
  import { catalogue, loadCatalogue } from '$lib/stores/technologies';
  import { certificationCatalogue, loadCertificationCatalogue } from '$lib/stores/certifications';
  import { signalCatalogue, loadSignalCatalogue } from '$lib/stores/signals';
  import { onMount } from 'svelte';
  import FilterBadge from './FilterBadge.svelte';
  import BlurredText from './Obfuscated.svelte';
  import { dataInClear } from '$lib/stores/auth';
  import { embeddingQuery } from '$lib/stores/selection';
  import { Database } from 'lucide-svelte';
  import { applicationMode } from '$lib/stores/mode';
  import { showDataSelectionModal } from '$lib/stores/modal';
  import { mixedResults, removeSimilarityBatch } from '$lib/stores/mixedResults';

  /**
   * The bar needs the technology catalogue for its labels.
   *
   * Loaded here as well as in the panel because the two are independent: a
   * customer arriving with technologies already set — a saved search, a link,
   * the assistant — sees this bar before ever opening the filter panel, and
   * without the catalogue the pills would read `mailchimp` rather than
   * Mailchimp. loadCatalogue is idempotent, so asking twice costs one request.
   */
  onMount(loadCatalogue);
  onMount(loadCertificationCatalogue);
  onMount(loadSignalCatalogue);

  $: signalLabels = new Map(
    ($signalCatalogue?.signals ?? []).map((k) => [k.slug, k.label] as const)
  );

  /** Slug to label, so a pill can say what the customer picked. */
  $: technologyLabels = new Map(
    ($catalogue?.groups ?? []).flatMap((group) =>
      group.technologies.map((tech) => [tech.slug, tech.label] as const)
    )
  );

  $: certificationLabels = new Map(
    ($certificationCatalogue?.groups ?? []).flatMap((group) =>
      group.certifications.map((c) => [c.slug, c.label] as const)
    )
  );

  $: gapLabels = new Map(
    ($catalogue?.mail_gaps ?? []).map((gap) => [gap.slug, gap.label] as const)
  );

  /**
   * One removable chip in the filter bar.
   *
   * Two shapes share the row: a plain filter, which shows `value`, and a
   * similarity batch, which shows the company it was seeded from and how many
   * results it contributed.
   */
  interface ActiveFilter {
    label: string;
    reset: () => void;
    value?: string;
    isSimilarity?: boolean;
    sourceCompanyId?: string;
    sourceCompanyName?: string;
    countDisplay?: string;
  }

  let activeFilters: ActiveFilter[] = [];

  $: {
    activeFilters = [];

    // The description leads, because it is the broadest cut: it decides which
    // companies are candidates at all, where the rest narrow whoever is left.
    // Shown truncated: it takes a sentence, and a badge that grew to hold one
    // would push every other filter off the bar.
    if ($embeddingQuery.trim()) {
      const text = $embeddingQuery.trim();
      activeFilters.push({
        label: $_('filters.description.title'),
        value: text.length > 40 ? `${text.slice(0, 40)}…` : text,
        reset: () => embeddingQuery.set('')
      });
    }

    if ($centerPostalCode && $radiusKm > 0) {
      activeFilters.push({
        label: $_('filterLabels.postalRadius'),
        value: `${$centerPostalCode} / ${$radiusKm}`,
        reset: () => {
          centerPostalCode.set('');
          radiusKm.set(0);
        }
      });
    }

    $postalCodes.forEach((code) => {
      activeFilters.push({
        label: $_('filterLabels.postalCode'),
        value: code,
        reset: () => postalCodes.set($postalCodes.filter((c) => c !== code))
      });
    });

    $cantons.forEach((canton) => {
      activeFilters.push({
        label: $_('filterLabels.canton'),
        value: canton,
        reset: () => cantons.set($cantons.filter((c) => c !== canton))
      });
    });

    $languages.forEach((lang) => {
      activeFilters.push({
        label: $_('filters.regionTypes.languages'),
        value: lang.toUpperCase(),
        reset: () => languages.set($languages.filter((l) => l !== lang))
      });
    });

    if ($companySizeRange[0] || $companySizeRange[1]) {
      const [min, max] = $companySizeRange;
      activeFilters.push({
        label: $_('filterLabels.employees'),
        value: `${min ?? '–'}–${max ?? '–'}`,
        reset: () => companySizeRange.set([null, null])
      });
    }

    if ($foundingYearRange[0] || $foundingYearRange[1]) {
      const [from, to] = $foundingYearRange;
      activeFilters.push({
        label: $_('filterLabels.founding'),
        value: `${from ?? '–'}–${to ?? '–'}`,
        reset: () => foundingYearRange.set([null, null])
      });
    }

    if ($branchOfficeCountMin > 0) {
      activeFilters.push({
        label: $_('filterLabels.branches'),
        value: `≥ ${$branchOfficeCountMin}`,
        reset: () => branchOfficeCountMin.set(0)
      });
    }

    if ($hasWebsite) {
      activeFilters.push({
        label: $_('data.website'),
        value: '',
        reset: () => hasWebsite.set(false)
      });
    }

    if ($hasEmail) {
      activeFilters.push({
        label: $_('data.email'),
        value: '',
        reset: () => hasEmail.set(false)
      });
    }

    if ($hasPhone) {
      activeFilters.push({
        label: $_('data.phone'),
        value: '',
        reset: () => hasPhone.set(false)
      });
    }

    if ($hasFacebook) {
      activeFilters.push({
        label: $_('data.facebook'),
        value: '',
        reset: () => hasFacebook.set(false)
      });
    }

    if ($hasLinkedin) {
      activeFilters.push({
        label: $_('data.linkedin'),
        value: '',
        reset: () => hasLinkedin.set(false)
      });
    }

    if ($hasInstagram) {
      activeFilters.push({
        label: $_('data.instagram'),
        value: '',
        reset: () => hasInstagram.set(false)
      });
    }

    if ($hasTwitter) {
      activeFilters.push({
        label: $_('data.twitter'),
        value: '',
        reset: () => hasTwitter.set(false)
      });
    }

    if ($hasRevisionsverzicht) {
      activeFilters.push({
        label: $_('data.auditExemption'),
        value: '',
        reset: () => hasRevisionsverzicht.set(false)
      });
    }

    // The form's name, never its register number: "3" on a pill is a number
    // the customer never typed and cannot read back.
    $legalForm.forEach((form) => {
      activeFilters.push({
        label: $_('data.legalForm'),
        value: $_(`filters.legalForm.name.${form}`, { default: form }),
        reset: () => legalForm.set($legalForm.filter((f) => f !== form))
      });
    });

    // One pill for the pair rather than two, because it is set as a pair and a
    // customer who clears "no sole proprietorships" means both halves of it.
    if ($excludeLegalForm.length > 0) {
      activeFilters.push({
        label: $_('filters.legalForm.excluded'),
        value: $excludeLegalForm
          .map((form) => $_(`filters.legalForm.name.${form}`, { default: form }))
          .join(', '),
        reset: () => excludeLegalForm.set([])
      });
    }

    if ($capital !== null) {
      activeFilters.push({
        label: $_('data.capital'),
        value: `≥ ${$capital}`,
        reset: () => capital.set(null)
      });
    }

    if ($revisionCompany) {
      activeFilters.push({
        label: $_('filterLabels.auditedBy'),
        value: $revisionCompany,
        reset: () => revisionCompany.set('')
      });
    }

    $textConstraints.forEach((term) => {
      activeFilters.push({
        label: $_('data.text'),
        value: term,
        reset: () => textConstraints.set($textConstraints.filter((t) => t !== term))
      });
    });

    // One pill per signal, with the timespan they share.
    const signalDays = $signalFilters?.days ?? $signalCatalogue?.default_days ?? 90;
    ($signalFilters?.selected ?? []).forEach((sel) => {
      const kind = signalLabels.get(sel.type) ?? sel.type;
      const amount = sel.min_amount
        ? ` ${$_('filters.signals.from')} CHF ${sel.min_amount.toLocaleString('de-CH')}`
        : '';
      activeFilters.push({
        label: $_('filters.signals.title'),
        value: `${kind}${amount} · ${$_('filters.signals.days', { values: { n: signalDays } })}`,
        reset: () => {
          const rest = ($signalFilters?.selected ?? []).filter((s) => s.type !== sel.type);
          signalFilters.set(rest.length > 0 ? { days: signalDays, selected: rest } : undefined);
        }
      });
    });

    // The earlier capital filter, as a search saved with it still carries it.
    if ($signalFilters?.capital_raised) {
      const signal = $signalFilters.capital_raised;
      let value = `${signal.occurrences}x`;
      if (signal.min_percentage > 0) value += `, +${signal.min_percentage}%`;
      if (signal.min_capital > 0) value += `, CHF ${signal.min_capital.toLocaleString('de-CH')}`;
      if (signal.from || signal.to) {
        const fromDate = signal.from ? new Date(signal.from).toLocaleDateString('de-CH') : '';
        const toDate = signal.to ? new Date(signal.to).toLocaleDateString('de-CH') : '';
        value += `, ${fromDate}-${toDate}`;
      }

      activeFilters.push({
        label: $_('filters.signals.types.capital_raised'),
        value,
        reset: () => signalFilters.set(undefined)
      });
    }

    // Add similarity batches as filter pills (one per source company)
    $mixedResults.similarityBatches.forEach((batch) => {
      // Show current visible count / original count for companies
      const visibleCompanyCount = batch.companyIds.length;
      const originalCompanyCount = batch.originalCompanyIds.length;

      // Show current visible count / original count for contacts
      const visibleContactCount = batch.contactIds?.length || 0;
      const originalContactCount = batch.originalContactIds?.length || 0;

      // Create display string showing both company and contact counts
      let countDisplay = '';
      if (visibleCompanyCount === originalCompanyCount) {
        countDisplay = `${visibleCompanyCount} companies`;
      } else {
        countDisplay = `${visibleCompanyCount}/${originalCompanyCount} companies`;
      }

      if (originalContactCount > 0) {
        if (visibleContactCount === originalContactCount) {
          countDisplay += `, ${visibleContactCount} contacts`;
        } else {
          countDisplay += `, ${visibleContactCount}/${originalContactCount} contacts`;
        }
      }

      activeFilters.push({
        label: $_('filters.similarTo'),
        sourceCompanyName: batch.sourceCompanyName,
        countDisplay,
        reset: () => removeSimilarityBatch(batch.sourceCompanyId),
        isSimilarity: true,
        sourceCompanyId: batch.sourceCompanyId
      });
    });

    // One pill per technology rather than one for all of them.
    //
    // They are chosen one at a time and dropped one at a time, and a customer
    // who picked six wants to drop the one that was wrong — a single pill
    // reading "Technologien: 6" can only be cleared wholesale. That is the
    // same reason legal forms get a pill each.
    //
    // The label falls back to the slug while the catalogue is in flight, which
    // is a moment at worst and better than a pill with no text in it.
    $technologies.forEach((slug) => {
      activeFilters.push({
        label: $_('filters.technologies.title'),
        value: technologyLabels.get(slug) ?? slug,
        reset: () => technologies.set($technologies.filter((s) => s !== slug))
      });
    });

    $mailGaps.forEach((slug) => {
      activeFilters.push({
        label: $_('filters.technologies.gapsTitle'),
        value: gapLabels.get(slug) ?? slug,
        reset: () => mailGaps.set($mailGaps.filter((s) => s !== slug))
      });
    });

    // The transport licence filter as one pill: its kinds and the minimum
    // say one thing together.
    if ($fleetFilter && $fleetFilter.kinds.length > 0) {
      const kinds = $fleetFilter.kinds.map((kind) => $_(`filters.fleet.${kind}`)).join(', ');
      activeFilters.push({
        label: $_('filters.fleet.title'),
        value: $fleetFilter.min_vehicles
          ? `${kinds} · ${$_('filters.fleet.atLeast', { values: { n: $fleetFilter.min_vehicles } })}`
          : kinds,
        reset: () => fleetFilter.set(null)
      });
    }

    // One pill per group role, so either can be dropped on its own.
    $groupRoles.forEach((role) => {
      activeFilters.push({
        label: $_('filters.group.title'),
        value: $_(`filters.group.${role}`),
        reset: () => groupRoles.set($groupRoles.filter((r) => r !== role))
      });
    });

    // One pill per sales model, so either can be dropped on its own.
    $salesModels.forEach((model) => {
      activeFilters.push({
        label: $_('filters.salesModel.title'),
        value: $_(`filters.salesModel.${model}`),
        reset: () => salesModels.set($salesModels.filter((m) => m !== model))
      });
    });

    // One pill per certification, as for the technologies.
    $certifications.forEach((slug) => {
      activeFilters.push({
        label: $_('filters.certifications.title'),
        value: certificationLabels.get(slug) ?? slug,
        reset: () => certifications.set($certifications.filter((s) => s !== slug))
      });
    });
  }
</script>

{#if activeFilters.length > 0 || $applicationMode === 'neighbor'}
  <div
    class="sticky top-0 z-10 flex w-full flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-4 py-2"
  >
    <!-- Data Pools Button - only show in neighbor mode -->
    {#if $applicationMode === 'neighbor'}
      <button
        on:click={() => showDataSelectionModal.set(true)}
        class="flex items-center space-x-2 rounded-full border-2 border-accent-200 bg-accent-50 !bg-none px-3 py-1 text-sm font-medium text-accent-700 !shadow-none transition hover:border-accent-300 hover:bg-accent-100"
      >
        <Database class="h-4 w-4" />
        <span>Data Pools ({$selectedIndustryCentroids.size})</span>
      </button>
    {/if}

    {#each activeFilters as filter (filter.label + (filter.value || filter.sourceCompanyId || ''))}
      {#if filter.isSimilarity}
        <FilterBadge label={filter.label} reset={filter.reset} isSimilarity={true}>
          <span class="whitespace-nowrap">
            {filter.label}: <BlurredText name={filter.sourceCompanyName} masked={!$dataInClear} /> ({filter.countDisplay})
          </span>
        </FilterBadge>
      {:else}
        <FilterBadge
          label={filter.label}
          value={filter.value}
          reset={filter.reset}
          isSimilarity={false}
        />
      {/if}
    {/each}

    {#if activeFilters.length > 1}
      <span
        on:click={() => activeFilters.forEach((f) => f.reset())}
        on:keydown={(e) => e.key === 'Enter' && activeFilters.forEach((f) => f.reset())}
        role="button"
        tabindex="0"
        class="inline-flex cursor-pointer select-none items-center rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:border-red-400 hover:text-red-600"
        style="background: none !important; background-color: transparent !important; box-shadow: none !important; border-radius: 9999px !important; padding: 0.25rem 0.75rem !important;"
      >
        ✕ {$_('buttons.clearAll')}
      </span>
    {/if}
  </div>
{/if}
